import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import useAdvancedSearchData from 'src/serverProxy/useAdvancedSearchData';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _capitalize from 'lodash/capitalize';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useLatestCumulativeReviewWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const reviewPeriodOptions = [
  { id: 'lifetime', label: 'Cumulative' },
  { id: 'weekly', label: 'Weekly' }
];

const mainMetricOptions = [
  { id: 'reviewVolume', label: 'Review Volume' },
  { id: 'rating', label: 'Review Rating' }
];

const fields = {
  lifetime: {
    reviewVolume: [{ name: 'lifetimeReviewVolume' }],
    rating: [
      { name: 'lifetimeReviewVolume' },
      { name: 'weightedLifetimeReviewRating' },
      { name: 'lifetimeReviewRating' }
    ]
  },
  weekly: {
    reviewVolume: [{ name: 'weeklyReviewVolume' }],
    rating: [{ name: 'weeklyReviewVolume' }, { name: 'weightedWeeklyReviewRating' }, { name: 'weeklyReviewRating' }]
  }
};

const responseKeyMap = {
  reviewVolume: 'ReviewVolume_sum',
  rating: 'ReviewRating_computed'
};

enum SubtitleDisplayName {
  rating = 'review rating',
  reviewVolume = 'review volume',
  weekly = '',
  lifetime = 'cumulative'
}

/**
 * Double Dropdown for (Cumulative/Weekly) and (Rating/RatingVolume) by Category/ SubCategory graph for Review tab.
 */

const ReviewDoubleDropdownTopEntityChart = ({ groupByField }: { groupByField: 'subCategoryId' | 'categoryId' }) => {
  const [selectedMainMetricField, setSelectedMainMetricField] = useState('reviewVolume');
  const [reviewPeriod, setReviewPeriod] = useState<'lifetime' | 'weekly'>('lifetime');
  const groupByKey = groupByField === 'subCategoryId' ? 'subcategory' : 'category';

  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const { data, isLoading } = useAdvancedSearchData({
    fields: fields[reviewPeriod][selectedMainMetricField],
    groupByField,
    indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2,
    requestId: `Beacon-review-tab-v2-double-dropdown-by-${groupByField}`,
    shouldFetchComparison: true,

    requestBuilder(builder, compare) {
      if (reviewPeriod === 'lifetime') {
        builder.modifyAggregation((aggBuilder) => {
          aggBuilder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
          return aggBuilder;
        });
        builder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
        if (compare) {
          builder.modifyAggregation((aggBuilder) => {
            aggBuilder.replaceConditionRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek);
            return aggBuilder;
          });
          builder.replaceConditionRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek);
        }
      }

      builder.addSortField(`ReviewOption`, fields[reviewPeriod][selectedMainMetricField], 'sum', false);

      return builder;
    }
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    data.forEach((datum) => {
      const categoryObj = datum?.metadata?.[groupByKey] ?? datum?.comparisonMetadata?.[groupByKey];
      const formattedCategoryObj = {
        name: categoryObj?.name,
        id: categoryObj?.id,
        type: categoryObj?.categoryType?.toLocaleLowerCase()
      };
      const responseKey = responseKeyMap[selectedMainMetricField];

      const currentDatum = datum?.values?.[`${reviewPeriod}${responseKey}_value`] ?? 0;
      const comparisonDatum = datum?.comparisonValues?.[`${reviewPeriod}${responseKey}_value`] ?? 0;

      if (!categoryObj || !categoryObj.name) {
        return;
      }

      comparisonData.push([categoryObj.name, comparisonDatum]);
      currentData.push([categoryObj.name, currentDatum]);
      categoryData.push(formattedCategoryObj);
    });
    return { currentData, categoryData, comparisonData };
  }, [data, groupByKey, reviewPeriod, selectedMainMetricField]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `${_capitalize(SubtitleDisplayName[reviewPeriod])} ${_capitalize(
          SubtitleDisplayName[selectedMainMetricField]
        )}`;
        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Reviews - ${label}`,
          metricLabel: label
        });
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${SubtitleDisplayName[reviewPeriod]} ${SubtitleDisplayName[selectedMainMetricField]} for ${entityPhrase} `}
            {taxonomyText} in each {groupByKey}
            {reviewPeriod === 'lifetime'
              ? '.'
              : ` during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center" gap={6}>
          <SlDropdownMenu
            buttonVariant="title"
            options={reviewPeriodOptions}
            selectedId={reviewPeriod}
            onChange={(option) => setReviewPeriod(option.id)}
            defaultLabel="Cumulative"
            open={false}
          />

          <SlDropdownMenu
            buttonVariant="title"
            options={mainMetricOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id)}
            defaultLabel="Review Volume"
            open={false}
          />
          <Text variant="h4">by {_capitalize(groupByKey)}</Text>
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: selectedMainMetricField === 'reviewVolume' ? METRICTYPE.VOLUME : METRICTYPE.DECIMAL
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: selectedMainMetricField === 'reviewVolume' ? METRICTYPE.VOLUME : METRICTYPE.DECIMAL
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ReviewDoubleDropdownTopEntityChart;
